import {
  Alert,
  Box,
  Divider,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { Dayjs } from 'dayjs'
import { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import KumocanButton from 'components/button'
import KumocanDialog from 'components/dialog'
import KumocanTextareaAutosize from 'components/textArea'
import KanseiTextField from 'components/textField'
import KumocanTimePicker from 'components/timePicker'
import { securityReportApi } from 'hooks/api/securityReport'
import { NewDate } from 'hooks/helper/date'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import i18n from 'i18n/config'
import { BUSINESS_DIVISION_OPTIONS } from 'types/contract'
import { QUALIFICATION_OMISSION_OPTIONS } from 'types/security'
import {
  SecurityReport,
  SecurityReportResult,
  CATEGORY_BUSINESS_DIVISIONS,
} from 'types/securityReport'
import { PRIMARY_COLOR } from 'valiable'

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

interface Form {
  id: string
  responsiblePersonSignature: string
  note: string
  securityReportResults: {
    id: string
    securityName: string
    qualifications: number[]
    securityId: string
    startTime: Dayjs
    endTime: Dayjs
    overTime: number
    restTime: number
  }[]
}

export default function SecurityReportPage(): JSX.Element {
  const { t } = useTranslation(['page-security-report', 'common'])
  const { showSnackbar } = useSnackbar()
  const query = useQuery()
  const [report, setReport] = useState<SecurityReport>()
  const [open, setOpen] = useState<boolean>(false)
  const [openUpdate, setOpenUpdate] = useState<boolean>(false)
  const [openSuccess, setOpenSuccess] = useState<boolean>(false)
  const [selectedBusinessDivisions, setSelectedBusinessDivisions] = useState<
    number[]
  >([])
  const [isChanged, setIsChanged] = useState(false)
  const [originalBusinessDivisions, setOriginalBusinessDivisions] = useState<
    number[]
  >([])
  const { register, handleSubmit, control, reset, getValues } = useForm<Form>({
    defaultValues: {
      securityReportResults: [],
    },
  })

  const handleClose = () => {
    setOpen(false)
  }

  const handleCloseUpdate = () => {
    setOpenUpdate(false)
  }

  const returnToTop = () => {
    location.href = '/assign/?slide=close'
  }

  const isItemSelected = (value: number) =>
    selectedBusinessDivisions.includes(value)

  const changeSelectedValue = (value: number) => {
    setSelectedBusinessDivisions((prev) => {
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]

      // sortして順番違いによる誤検知を防ぐ
      const sortedNext = [...next].sort((a, b) => a - b)
      const sortedOriginal = [...originalBusinessDivisions].sort(
        (a, b) => a - b,
      )
      const hasChanged =
        sortedNext.length !== sortedOriginal.length ||
        sortedNext.some((v, i) => v !== sortedOriginal[i])
      setIsChanged(hasChanged)

      return next
    })
  }
  const fetchData = useCallback(
    (detailId: string, date: Dayjs) => {
      securityReportApi()
        .findByDetailIdAndDate(detailId, date)
        .then((res) => {
          setReport(res.data)

          const businessDivisions = res.data.businessDivisions
          setOriginalBusinessDivisions(businessDivisions)
          setSelectedBusinessDivisions(businessDivisions)
          reset({
            id: res.data.id,
            responsiblePersonSignature:
              res.data.responsiblePersonSignature.trim(),
            note: res.data.note,
            securityReportResults: res.data.securityReportResults?.map(
              (result) => ({
                id: result.id,
                securityName: `${result.security?.lastName || ''} ${result.security?.firstName || ''}`,
                qualifications: result?.qualifications,
                securityId: result.securityId,
                startTime: result.startTime,
                endTime: result.endTime,
                restTime: result.restTime,
                overTime: result.overTime,
              }),
            ),
          })
        })
    },
    [reset],
  )

  const qualificationLabel = (qs: number[]) => {
    if (!report) return null

    if (report?.qualifications?.length === 0) {
      return <>{t('qualification_none')}</>
    }
    return (
      <>
        {report?.qualifications?.map((qualification) => {
          const isChecked = qs.includes(qualification)
          return (
            <span key={qualification}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <input type="checkbox" checked={isChecked} readOnly />
                {t(
                  `qualification_labels.${qualification}` as Parameters<
                    typeof t
                  >[0],
                  QUALIFICATION_OMISSION_OPTIONS.find(
                    (x) => x.value === qualification,
                  )?.label ?? '',
                )}
              </label>
            </span>
          )
        })}
      </>
    )
  }

  const signed = !!report?.responsiblePersonSignature

  const updateReport = async (form: Form) => {
    const signature = getValues('responsiblePersonSignature').trim()
    if (signature === '') {
      showSnackbar(t('signature_required'))
      return
    }
    // 業務区分の変更があった場合、noteに自動コメントを追加
    let note = form.note
    if (isChanged) {
      const autoComment = t('auto_comment')
      if (!note.includes(autoComment)) {
        note = `${note}\n\n${autoComment}`
      }
    }

    const res: SecurityReportResult[] = form.securityReportResults.map((x) => {
      return {
        id: x.id,
        securityId: x.securityId,
        securityReportId: form.id,
        qualifications: x.qualifications,
        startTime: x.startTime,
        endTime: x.endTime,
        restTime: x.restTime,
        overTime: x.overTime,
      }
    })

    const divisionBitflag = selectedBusinessDivisions.reduce(
      (acc, cur) => acc | cur,
      0,
    )
    return securityReportApi().sign(
      form.id,
      form.responsiblePersonSignature,
      note,
      divisionBitflag,
      res,
    )
  }

  const sign = async (form: Form): Promise<void> => {
    if (!report) {
      return
    }

    updateReport(form).then(() => {
      setOpenSuccess(true)
    })
  }

  // 業務区分の更新
  const updateBusinessDivision = async () => {
    // report を shallow copy + businessDivisions のみ更新
    setReport((prev) =>
      prev ? { ...prev, businessDivisions: selectedBusinessDivisions } : prev,
    )

    setOpenUpdate(false)
  }

  const { fields } = useFieldArray({
    control,
    name: 'securityReportResults',
  })

  useEffect(() => {
    const detailId = query.get('contract_detail_id')
    if (detailId) {
      fetchData(detailId, NewDate(query.get('date')))
    } else {
      returnToTop()
    }
  }, [fetchData])

  return (
    <Box>
      {signed && (
        <Alert
          severity="success"
          sx={{
            margin: '10px auto 0',
            width: '80%',
          }}
        >
          {t('signed')}
        </Alert>
      )}
      <Box sx={{ marginBottom: '50px' }}>
        <Box
          sx={{
            borderBottom: `solid 2px ${PRIMARY_COLOR}`,
            margin: '10px 20px 0',
            fontWeight: 'bold',
          }}
        >
          {t('page_title')}
        </Box>
        <Box component="form" onSubmit={handleSubmit(sign)}>
          <Grid
            container
            rowGap={2}
            sx={{ marginTop: '20px', padding: '0 20px' }}
          >
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {report?.date.format(t('common:date_format.date_with_day'))}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={titleStyle}>{t('client_name')}</Typography>
              <Typography>{report?.contractName}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={titleStyle}>{t('site_address')}</Typography>
              <Typography>{report?.address}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={titleStyle}>{t('business_division')}</Typography>
              <Typography>
                {getBusinessDivisionLabels(
                  selectedBusinessDivisions ?? [],
                  t,
                ).join(i18n.language === 'ja' ? '、' : ', ')}
              </Typography>
              <Box
                sx={{
                  marginTop: '16px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <KumocanButton
                  style={{
                    minWidth: '100px',
                    fontSize: '16px',
                  }}
                  onClick={() => setOpenUpdate(true)}
                >
                  {t('edit')}
                </KumocanButton>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>

          <Grid
            container
            rowGap={2}
            sx={{ marginTop: '20px', padding: '0 20px' }}
          >
            <Grid item xs={12}>
              <h3 style={{ fontSize: '12px', color: 'grey' }}>
                {t('security_info')}
              </h3>
            </Grid>
            {fields.map((field, index) => (
              <Box key={field.id}>
                <Divider
                  sx={{
                    marginBottom: '10px',
                    borderStyle: 'dotted',
                    borderColor: 'grey',
                    borderWidth: '1px',
                  }}
                />
                <Grid container rowGap={1.5}>
                  <Grid item xs={4}>
                    <Typography>No</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {index + 1}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('name')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {field.securityName}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('qualification')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {qualificationLabel(field.qualifications)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('start_time')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <KumocanTimePicker
                      name={`securityReportResults.${index}.startTime`}
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('end_time')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <KumocanTimePicker
                      name={`securityReportResults.${index}.endTime`}
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('rest_time')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <KanseiTextField
                      id={`${index}`}
                      register={register(
                        `securityReportResults.${index}.restTime`,
                        { valueAsNumber: true },
                      )}
                      type="decimal"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{t('overtime')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <KanseiTextField
                      id={`${index}`}
                      register={register(
                        `securityReportResults.${index}.overTime`,
                        { valueAsNumber: true },
                      )}
                      type="decimal"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Grid item xs={12}>
              <Typography>{t('notes')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <KumocanTextareaAutosize
                id="note"
                style={{
                  width: '100%',
                  height: '100px',
                  maxWidth: '100%',
                  borderColor: '#80808057',
                  borderRadius: '5px',
                }}
                register={register('note')}
              ></KumocanTextareaAutosize>
            </Grid>
            <Box sx={signStyle}>
              <Box sx={signContentStyle}>
                <Typography>{t('signature_label')}</Typography>
                <KanseiTextField
                  id="responsiblePersonSignature"
                  register={register('responsiblePersonSignature')}
                  style={{ backgroundColor: 'white', marginTop: '10px' }}
                ></KanseiTextField>
                <KumocanButton
                  style={{
                    width: '100%',
                    height: '50px',
                    fontSize: '14px',
                    marginTop: '20px',
                  }}
                  disabled={signed}
                  onClick={() => setOpen(true)}
                >
                  {t('submit')}
                </KumocanButton>
              </Box>
            </Box>
            <KumocanDialog open={openSuccess}>
              <Typography sx={{ textAlign: 'center', fontSize: '20px' }}>
                {t('signed_message')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '20px',
                }}
              >
                <KumocanButton
                  style={{
                    minWidth: '100px',
                    fontSize: '16px',
                  }}
                  onClick={returnToTop}
                >
                  {t('common:buttons.back')}
                </KumocanButton>
              </Box>
            </KumocanDialog>{' '}
            <KumocanDialog open={open} handleClose={handleClose}>
              <Typography sx={{ marginBottom: '10px' }}>
                {t('confirm_message')}
              </Typography>
              <span>
                {t('responsible_person')}{' '}
                <strong>{getValues('responsiblePersonSignature')}</strong>
              </span>
              <Box sx={dialogActionsStyle}>
                <KumocanButton
                  onClick={handleClose}
                  variant="outlined"
                  style={{ minWidth: '100px', fontSize: '16px' }}
                >
                  {t('common:buttons.no')}
                </KumocanButton>
                <KumocanButton
                  buttonType="submit"
                  style={{ minWidth: '100px', fontSize: '16px' }}
                  onClick={handleSubmit(sign)}
                >
                  {t('common:buttons.yes')}
                </KumocanButton>
              </Box>
            </KumocanDialog>
            <KumocanDialog
              open={openUpdate}
              handleClose={handleCloseUpdate}
              minWidth={'350px'}
            >
              <Typography sx={dialogTitleStyle}>
                {t('business_division_setting')}
              </Typography>
              <Box style={{ paddingBottom: '40px' }}>
                {CATEGORY_BUSINESS_DIVISIONS.map((category) => (
                  <Box key={category.title}>
                    <Typography sx={categoryTitleStyle}>
                      {t(
                        `business_division_categories.${category.key}`,
                        category.title,
                      )}
                    </Typography>
                    <Box sx={categoryItemsStyle}>
                      {category.items.map((item) => (
                        <Box
                          key={item.value}
                          sx={
                            category.items.indexOf(item) ===
                            category.items.length - 1
                              ? categoryItemStyle
                              : {
                                  ...categoryItemStyle,
                                  borderBottom: '3px dotted #f3f4f6',
                                }
                          }
                        >
                          <FormControlLabel
                            key={item.value}
                            label={
                              <Typography
                                sx={{ width: '100%', fontSize: '20px' }}
                              >
                                {t(
                                  `business_division_items.${item.value}`,
                                  item.label,
                                )}
                              </Typography>
                            }
                            checked={isItemSelected(item.value)}
                            control={
                              <Checkbox
                                id={String(item.value)}
                                onChange={() => changeSelectedValue(item.value)}
                              />
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={fixedDialogActionsStyle}>
                <KumocanButton
                  onClick={handleCloseUpdate}
                  variant="contained"
                  color={'inherit'}
                  style={{ width: '50%', fontSize: '16px' }}
                >
                  {t('common:buttons.cancel')}
                </KumocanButton>
                <KumocanButton
                  style={{ width: '50%', fontSize: '16px' }}
                  onClick={updateBusinessDivision}
                >
                  {t('common:buttons.submit')}
                </KumocanButton>
              </Box>
            </KumocanDialog>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

const signStyle: CSSProperties = {
  width: '100%',
  backgroundColor: '#8adad6',
  borderRadius: '4px',
  opacity: 0.8,
}

const signContentStyle: CSSProperties = {
  opacity: 1,
  width: '200px',
  margin: '20px auto 20px',
  textAlign: 'center',
}

const titleStyle: CSSProperties = {
  fontSize: '10px',
  color: 'grey',
}

const dialogActionsStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  gap: '8px',
  justifyContent: 'space-between',
  marginTop: '20px',
}

const fixedDialogActionsStyle: CSSProperties = {
  position: 'fixed',
  bottom: '40px',
  marginTop: '20px',
  left: '0',
  right: '0',
  width: '315px',
  margin: '0 auto',
  display: 'flex',
  gap: '8px',
  justifyContent: 'space-between',
}

const dialogTitleStyle: CSSProperties = {
  marginBottom: '8px',
  paddingBottom: '8px',
  fontSize: '24px',
  borderBottom: '1px solid #ccc',
}

const categoryTitleStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '20px',
}

const categoryItemsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px 0',
  marginBottom: '8px',
  borderBottom: '1px solid #ccc',
}

const categoryItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 0',
  fontSize: '20px',
}

// 業務区分のbit -> labelに

export const getBusinessDivisionLabels = (
  values: number[],
  t: any,
): string[] => {
  const allChildren = BUSINESS_DIVISION_OPTIONS.flatMap(
    (group) => group.children,
  )

  return allChildren
    .filter((child) => values.includes(Number(child.value)))
    .map((child) => t(`business_division_items.${child.value}`, child.label))
}
